import { ITabValues } from 'common/api/type-def';

import {
	genControlStatement,
	genControlTestScript,
	generateAISimulationPromptResult,
	generateAISimulationResult,
	generateControlTestPromptResult,
	genPromptControlStatement,
} from 'features/AiSimulation/api';
import { ToolbarType } from 'features/AiSimulation/Components/SectionToolbar/SectionToolbar';

export const CONTROL_TYPE = [
	{
		label: 'Automatic',
		value: 'Automatic',
	},
	{
		label: 'Manual',
		value: 'Manual',
	},
	{
		label: 'IT Dependent Manual',
		value: 'IT Dependent Manual',
	},
];

export const CONTROL_FREQUENCY = [
	{
		label: 'Daily',
		value: 'Daily',
	},
	{
		label: 'Weekly',
		value: 'Weekly',
	},
	{
		label: 'Monthly',
		value: 'Monthly',
	},
	{
		label: 'Quarterly',
		value: 'Quarterly',
	},
	{
		label: 'Bi-Annually',
		value: 'Bi-Annually',
	},
	{
		label: 'Annually',
		value: 'Annually',
	},
	{
		label: 'Adhoc',
		value: 'Adhoc',
	},
];

export enum AI_SIMULATION_MODEL_KEYS {
	PLAIN_ENGLISH_SUMMARY_KEY = 'plain-english-summary',
	REGULATION_SUMMARY_KEY = 'regulatory-summary-sa',
	HORIZON_SCANNING_KEY = 'regulatory-summary-hs',
	HORIZON_CHANGE_SUMMARY_KEY = 'change-summary',
	RISK_STATEMENT_KEY = 'risk-statement',
	CONTROL_OBJECTIVE_KEY = 'control-objective',
	CONTROL_STATEMENT_KEY = 'control-statement',
	CONTROL_TEST_SCRIPTS_KEY = 'control-test-scripts',
	OBLIGATION_TO_POLICY_KEY = 'obligation-to-policy',
	OBLIGATION_GENERATION_KEY = 'obligation-generation',
	CONTROL_QUALITY_GRAMMAR_CHECK_KEY = 'control-quality-grammar-check',
	CONTROL_LANGUAGE_QUALITY_KEY = 'language-quality-check',
	CONTROL_RECOMMENDATION = 'control-recommendation',
}

export enum AI_SIMULATION_TYPES {
	PLAIN_ENGLISH_SUMMARY = 'Plain english summary',
	REGULATION_SUMMARY = 'Regulation summary',
	RISK_STATEMENT = 'Risk statement',
	CONTROL_TEST_SCRIPTS = 'Control test scripts',
	CONTROL_STATEMENT = 'Control statement',
	CONTROL_QUALITY_GRAMMAR_CHECK = 'Control Quality and Grammar Check',
	CONTROL_OBJECTIVE = 'Control objective',
	OBLIGATION_TO_POLICY = 'Obligation to Policy',
	PLAIN_ENGLISH_SUMMARY_BUTTON = 'plain english summary',
	REGULATION_SUMMARY_BUTTON = 'regulation summary',
	RISK_STATEMENT_BUTTON = 'risk statement',
	CONTROL_TEST_SCRIPTS_BUTTON = 'control test scripts',
	CONTROL_STATEMENT_BUTTON = 'control statement',
	CONTROL_OBJECTIVE_BUTTON = 'control objective',
	REGULATION_OBLIGATION_SUMMARY = 'regulation-obligation-control',
	OBLIGATION_TO_POLICY_SUMMARY = 'obligation-to-policy',
}

export enum AI_TYPE {
	MODEL = 'model',
	RECIPE = 'recipe',
	SHARED = 'shared',
}

export enum AI_ACTIONS_BTN {
	EDIT = 'EDIT',
	SHARE = 'SHARE',
	DOWNLOAD = 'DOWNLOAD',
	DELETE = 'DELETE',
}

export enum AI_ACTIONS {
	SAVE = 'SAVE',
	SHARE = 'SHARE',
}

export const AiRecipePageTitle =
	'Regulation obligations and control generation';

export enum COPYANDPASTE_TEXT {
	HEADING1 = "Let's get started",
	HEADING2 = 'Copy and paste regulation information below',
	HEADING3 = "Select a regulation from PwC's massive curated library or custom library.",
	NOTIFICATION_MSG = 'You have exceeded the maximum character limit. Please reduce the amount of selections to continue.',
}
export enum RiskChatHistoryGroup {
	TODAY = 'Today',
	YESTERDAY = 'Yesterday',
	PAST7DAYS = 'Past 7 days',
	PAST30DAYS = 'Past 30 days',
}

export enum AI_SIMULATION_TITLE {
	SIMULATION_ARENA = 'Simulation arena',
	AI_RECIPES = 'AI recipes',
	MODELS = 'Models',
	MY_SIMULATIONS = 'My simulations',
}

const {
	PLAIN_ENGLISH_SUMMARY,
	RISK_STATEMENT,
	CONTROL_OBJECTIVE,
	CONTROL_STATEMENT,
	CONTROL_TEST_SCRIPTS,
} = AI_SIMULATION_TYPES;

const {
	PLAIN_ENGLISH_SUMMARY_KEY,
	RISK_STATEMENT_KEY,
	CONTROL_OBJECTIVE_KEY,
	CONTROL_STATEMENT_KEY,
	CONTROL_TEST_SCRIPTS_KEY,
} = AI_SIMULATION_MODEL_KEYS;

export const aiDefaultStepData = [
	{
		title: 'Insert regulation or regulatory text',
		id: 'PlainEnglishSummary',
		workflowStep: PLAIN_ENGLISH_SUMMARY,
		aiModelKey: PLAIN_ENGLISH_SUMMARY_KEY,
		inputValue: '',
		outputTitle: 'Plain english summary',
		outputValue: '',
		generatorFn: generateAISimulationResult,
		generatePromptSelect: generateAISimulationPromptResult,
	},
	{
		title: 'Plain english summary',
		id: 'RiskStatement',
		workflowStep: RISK_STATEMENT,
		aiModelKey: RISK_STATEMENT_KEY,
		inputValue: '',
		outputTitle: 'Risk statement',
		outputValue: '',
		generatorFn: generateAISimulationResult,
		generatePromptSelect: generateAISimulationPromptResult,
	},
	{
		title: 'Plain english summary',
		id: 'ControlObjective',
		workflowStep: CONTROL_OBJECTIVE,
		aiModelKey: CONTROL_OBJECTIVE_KEY,
		inputValue: '',
		outputTitle: 'Control objective',
		outputValue: '',
		generatorFn: generateAISimulationResult,
		generatePromptSelect: generateAISimulationPromptResult,
	},
	{
		title: 'Plain english summary',
		id: 'ControlStatement',
		workflowStep: CONTROL_STATEMENT,
		aiModelKey: CONTROL_STATEMENT_KEY,
		inputValue: '',
		outputTitle: 'Control statement',
		outputValue: '',
		generatorFn: genControlStatement,
		generatePromptSelect: genPromptControlStatement,
	},
	{
		title: 'Control statement',
		id: 'ControlTestScript',
		workflowStep: CONTROL_TEST_SCRIPTS,
		aiModelKey: CONTROL_TEST_SCRIPTS_KEY,
		inputValue: '',
		outputTitle: 'Control test scripts',
		outputValue: '',
		generatorFn: genControlTestScript,
		generatePromptSelect: generateControlTestPromptResult,
	},
];

export const aiProgressBarElements = [
	{
		index: 0,
		value: 4,
		label: 'Plain English Summary',
	},
	{
		index: 1,
		value: 30,
		label: 'Risk Statement',
	},
	{
		index: 2,
		value: 52,
		label: 'Control Objective',
	},
	{
		index: 3,
		value: 75,
		label: 'Control Statement',
	},
	{
		index: 4,
		value: 96,
		label: 'Control Test Scripts',
	},
];

export const aiModelTabs: ITabValues[] = [
	{
		name: 'Create',
	},
	{
		name: 'Improve',
	},
	{
		name: 'Summarize',
	},
	{
		name: 'Q&A',
		hideSentenceCase: true,
	},
];

export enum AI_SIMULATION_GENAI_DISCLAIMER {
	DISCLAIMER = 'AI-generated content may be inaccurate and requires human review.',
}

export const modelSecTools = [
	ToolbarType.SEARCH,
	ToolbarType.SORT,
	ToolbarType.FILTER,
];

export const simulationSecTools = [
	ToolbarType.SEARCH,
	ToolbarType.FILTER,
	ToolbarType.TOGGLE_VIEW,
];

export enum AI_SIMULATION_REG_SEARCH_TABS {
	SEARCH = 'Search',
	COPY = 'Copy-and-paste',
}

export enum AI_SIMULATION_COPYPASTE_WORDLIMIT {
	TWELVE_THOUSAND = 12000,
}

export enum AI_SIMULATION_COPYPASTE_ERRORMSG {
	LARGE_FILE = 'File is larger than 10 MB',
	UPLOAD_FAIL = 'Unable To Upload the file.',
	DOC_TYPE = 'File type must be PDF, DOC or DOCX',
}

export const DEFAULT_AI_SUMMARY_MAX_LIMIT = 131070;

export const CONTROL_STATEMENT_DEFAULT_OPTIONS = 'Select an option';

export const PROMPT_SUCCESS_MSG =
	'Successfully enabled as default prompt for AI model.';

export const OBLIGATION_STOP_GENERATING = 'Stop generating';
export const OBLIGATION_STOPPING = 'Stopping';
export const CONTROL_QUALITY_HEADER =
	'Quality assurance - language quality check';
export const CONTROL_QUALITY_CONTENT_LABEL = 'Content';
export const CONTROL_QUALITY_GENERATE_RESULTS = 'Generate results';
export const CONTROL_QUALITY_GENERATE_CONTROL = 'Generate updated control';
export const CONTROL_QUALITY_RESULTS_DETAILS = 'Result details';
export const CONTROL_QUALITY_ADDITIONAL_LABEL =
	'Enter additional information to submit for updated control description';

export const CONTROL_5W_LABELS = {
	What: 'What',
	When: 'When',
	Where: 'Where',
	Who: 'Who',
	Why: 'Why',
};

export const OBLIGATION_RESULT_TYPE = [
	{
		displayValue: 'Match',
		result: 'Match',
		class: 'accordian-match',
		displayOrder: 1,
	},
	{
		displayValue: 'Partial',
		result: 'Partial Match',
		class: 'accordian-partial',
		displayOrder: 2,
	},
	{
		displayValue: 'No match',
		result: 'No Match',
		class: 'accordian-not-match',
		displayOrder: 3,
	},
];
export const CONTROL_QUALITY_GRAMMAR_CHECK_COMP = {
	FILE_UPLOAD_DROP_AREA_TITLE: 'Batch upload and process',
	FILE_UPLOAD_DROP_AREA_SUBTITLE:
		'Upload file, map fields, and receive results via email',
	DRAG_AREA_TITLE: (maxfileSize: number) =>
		`You can upload CSV and XLSX file. The max file size is ${maxfileSize}MB.`,
	ACCEPTED_FILE_TYPE: {
		'text/csv': ['.csv'],
		'application/vnd.ms-excel': ['.xls', '.xlsx'],
	},
	MAX_FILE_SIZE: 10,
	MAX_FILE_LIMIT: 1,
};

export const BATCH_UPLOAD = 'Batch upload and process';

export interface IColumnMappingData {
	label: string;
	value: string[];
	fileInfo?: File;
	key: string;
}
export const CONTROL_COLUMN_MAPPING_DATA: IColumnMappingData[] = [
	{
		key: 'controlDescription',
		value: [],
		label: 'Control statement',
		fileInfo: undefined,
	},
];
export const PLAIN_ENGLISH_SUMMARY_COLUMN_MAPPING_DATA: IColumnMappingData[] = [
	{
		key: 'regulationText',
		value: [],
		label: 'Regulation Text',
		fileInfo: undefined,
	},
];
export const REGULATION_SUMMARY_COLUMN_MAPPING_DATA: IColumnMappingData[] = [
	{
		key: 'regulationText',
		value: [],
		label: 'Regulation Text',
		fileInfo: undefined,
	},
	{
		key: 'institution',
		value: [],
		label: 'Institution',
		fileInfo: undefined,
	},
];
export const REQUIRED_FIELD = 'Required field';
export const COLUMNS_IN_YOUR_FILE = 'Columns in your file';
export const SELECT_COLUMN_LABEL = 'Select Column';

export const AI_SIMULATION_BULK_UPLOAD_ERROR_MSG =
	'We have encountered unexpected problems. Please, ';
export const AI_SIMULATION_BULK_UPLOAD_ERROR_TRY_AGAIN_MSG = ' try again.';
export const CONTROL_FILE_UPLOAD_SUCCESS_MAIN_LABEL =
	'File successfully submitted for processing!';
export const CONTROL_FILE_UPLOAD_SUCCESS_LABEL =
	'An email with the processed results will be sent shortly.';
export const FILE_UPLOAD_REQUIRED_FIELDS = 'Map columns with required fields';
export const CONTROL_5W_TYPE = 'Control5W';
export const MAXIMUM_RETRY_ALERT =
	'You have tried the maximum number of attempts. Please upload another file.';

export const CONTROL_QUALITY_COPY_PASTE = {
	TITLE: 'Single input and process',
	SUB_TITLE: 'Copy and paste one record for analysis.',
};

export const COLUMN_HEADERS_DOES_NOT_EXIST =
	'Unable to read the file. Please recheck and try again.';

export const REGULATION_SUMMARY_TYPE = 'RegulationSummary';
export const PLAIN_ENGLISH_SUMMARY_TYPE = 'PlainEnglishSummary';

export enum LOCATION_OPTIONS {
	HORIZON_SCANNING = 'Horizon scanning',
	PRC_INVENTORY = 'PRC inventory',
	REQ_INV = 'Requirement inventory',
	SIM_ARENA = 'Simulation arena',
}
